import React, { useState } from "react";
import { Table, Header, Message, Statistic, Dropdown, Segment, Label, Input } from "semantic-ui-react";
import { PROCESOS_CON_BENCHMARK, ADVERTENCIAS_BENCHMARK_IFC } from "../catalogs/benchmarks";
import { TIPOLOGIAS } from "../catalogs/tipologias";
import { CASOS_DE_CAMPO } from "../catalogs/casosDeCampo";
import { CLASES_RESIDUO } from "../catalogs/clasificacionResiduo";
import { estiloDeClave } from "../catalogs/semaforoColores";
import { CON_RESPALDO_PUBLICADO, CRITERIO_PROPIO_SUSTEMIA, EL_MODULO_SE_NIEGA_A } from "../catalogs/fuentesYCriterios";
import { evalBenchmark, costearCentros, costoDeLaPerdida, META_INDICE_CALIDAD } from "../waterBalanceCalc";
import { useWaterBalanceT } from "../useWaterBalanceT";

const PROCESO_OPTIONS = Object.values(PROCESOS_CON_BENCHMARK)
  .filter((p) => p.bench)
  .map((p) => ({ key: p.id, value: p.id, text: p.nombre }));

// El motor (waterBalanceCalc.js) no se tradujo: sigue esperando
// origen/volumenM3/centroId y tarifas en español. Misma frontera de
// traducción que estado.js#buildEstadoParaMotor.
const tarifasParaMotor = (t) => ({
  aguaPorM3: t?.waterPerM3,
  energiaTermicaPorM3: t?.thermalEnergyPerM3,
  quimicosPorM3: t?.chemicalsPerM3,
  tratamientoPorM3: t?.treatmentPerM3,
});

// Números de sección reales, tal como los define la hoja "08 Informe" de
// Sustemia_BalanceHidrico_Spec_ES_v1.xlsx. Esa hoja documenta 18 de las 28
// secciones de la estructura original (01,02,03,06,07,08,09,11,12,13,14,15,
// 16,20,22,24,27,28) — las diez restantes (04,05,10,17,18,19,21,23,25,26) no
// están definidas en ningún documento disponible. Se numera con los saltos
// reales en vez de renumerar seguido, para no ocultar el hueco: es la misma
// honestidad que el módulo aplica a cualquier dato que no tiene de dónde
// salir (Fuentes.md §3: "no se aproxima, se declara la ausencia").
export function InformeReport({
  typology,
  period,
  year,
  resultado,
  production,
  executedFieldCases,
  costs,
  centers,
  outputs,
  instruments,
  inputs,
  lossClassification,
  correctionProposals,
  context,
  hasLaundry,
}) {
  const t = useWaterBalanceT();
  const [procesoId, setProcesoId] = useState(PROCESO_OPTIONS[0]?.value);
  const [declaracion, setDeclaracion] = useState({ nombre: "", cargo: "", fecha: "" });
  const tipologiaCfg = TIPOLOGIAS[typology];

  const benchmarkDisponible = tipologiaCfg?.benchmark?.disponible;
  const proceso = PROCESOS_CON_BENCHMARK[procesoId];
  const esp = production?.value ? (resultado.totalEntradas * 1000) / production.value : null;
  const evalIndicador = benchmarkDisponible ? evalBenchmark(proceso, esp) : { disponible: false, motivo: tipologiaCfg?.benchmark?.motivo };

  const clasificado = (lossClassification || []).reduce((a, c) => a + (Number(c.volumeM3) || 0), 0);
  const avanceClasificacion = resultado.descuadre > 0 ? Math.min(100, (clasificado / resultado.descuadre) * 100) : 100;

  const outputsParaMotor = (outputs || []).map((s) => ({ centroId: s.centerId, nombre: s.name, origen: s.origin, volumenM3: s.volumeM3 }));
  const clasificacionParaMotor = (lossClassification || []).map((c) => ({ categoria: c.category, volumenM3: c.volumeM3 }));
  // Cobertura y Pareto (motor de cálculo §14/§15) ya vienen calculados por
  // recalcularBalance con el residuo "sin_atribuir" correcto: no se
  // recalculan acá aparte.
  const cobertura = resultado.cobertura;
  // §07: "proceso" de un instrumento no es un campo propio (un instrumento
  // no se declara "para" un proceso) — se deriva de qué salida lo usa como
  // instrumentId. Un instrumento sin ninguna salida vinculada queda
  // "sin asignar": no se inventa un proceso que no está declarado.
  const procesoDeInstrumento = (instrumentoId) => {
    const usos = (outputs || []).filter((s) => s.instrumentId === instrumentoId).map((s) => s.name).filter(Boolean);
    return usos.length ? usos.join(", ") : t("water_balance_not_declared");
  };
  const porCentro = costearCentros(outputsParaMotor, tarifasParaMotor(costs?.tariffs), (costs?.perCenter || []).map((tc) => ({ centroId: tc.centerId, ...tarifasParaMotor(tc) })));
  const totalCosteo = porCentro.reduce((a, c) => a + c.total, 0);
  const perdidaCosteada = costoDeLaPerdida(clasificacionParaMotor, tarifasParaMotor(costs?.tariffs));

  const SIN_DECLARAR = t("water_balance_not_declared");
  const COBERTURA_LABEL = {
    medido: t("water_balance_coverage_measured"),
    estimado: t("water_balance_coverage_estimated"),
    sin_atribuir: t("water_balance_coverage_unattributed"),
    reparto: t("water_balance_coverage_reparto"),
  };

  // §24 Trazabilidad metodológica — una fila por compromiso que el propio
  // módulo documenta (Fuentes.md), no una lista de cláusulas ISO inventada.
  // Lo no cubierto aparece como pendiente, tal como pide el spec.
  const requisitos = [
    { req: t("water_balance_req_volume_disabled"), cubierto: true },
    { req: t("water_balance_req_grade_derived"), cubierto: true },
    { req: t("water_balance_req_no_partial_verdict"), cubierto: true },
    { req: t("water_balance_req_grade5_evidence"), cubierto: true },
    { req: t("water_balance_req_loss_classified"), cubierto: !resultado.bloqueos.residuoSinClasificar },
    { req: t("water_balance_req_divergences_resolved"), cubierto: !resultado.bloqueos.divergenciasSinResolver },
    { req: t("water_balance_req_mfca_costing"), cubierto: Object.values(costs?.tariffs || {}).some((v) => v !== "" && v != null) },
    { req: t("water_balance_req_persistence"), cubierto: true },
    { req: t("water_balance_req_baseline"), cubierto: false, nota: t("water_balance_req_baseline_note") },
    { req: t("water_balance_req_full_report"), cubierto: false, nota: t("water_balance_req_full_report_note") },
  ];

  return (
    <div className="water-balance-step">
      <Header as="h2">{t("water_balance_report_title")}</Header>
      <Message info size="small">{t("water_balance_report_numbering_note")}</Message>

      <Header as="h3">{t("water_balance_section_01")}</Header>
      <Table celled compact>
        <Table.Body>
          <Table.Row><Table.Cell>{t("water_balance_stat_entries")}</Table.Cell><Table.Cell textAlign="right">{resultado.totalEntradas.toLocaleString("es-AR")} m³</Table.Cell></Table.Row>
          <Table.Row><Table.Cell>{t("water_balance_stat_outputs")}</Table.Cell><Table.Cell textAlign="right">{resultado.totalSalidas.toLocaleString("es-AR")} m³</Table.Cell></Table.Row>
          <Table.Row><Table.Cell>{t("water_balance_col_delta_inventory")}</Table.Cell><Table.Cell textAlign="right">{resultado.deltaInventarioM3.toLocaleString("es-AR")} m³</Table.Cell></Table.Row>
          <Table.Row>
            <Table.Cell><strong>{t("water_balance_stat_imbalance")}</strong></Table.Cell>
            <Table.Cell textAlign="right"><strong>{resultado.descuadre.toLocaleString("es-AR")} m³ · {resultado.descuadrePct.toFixed(1)}%</strong></Table.Cell>
          </Table.Row>
          <Table.Row><Table.Cell>{t("water_balance_data_quality_index")}</Table.Cell><Table.Cell textAlign="right">{resultado.indiceCalidad.toFixed(2)} / 5</Table.Cell></Table.Row>
          <Table.Row><Table.Cell>{t("water_balance_classification_progress")}</Table.Cell><Table.Cell textAlign="right">{avanceClasificacion.toFixed(0)}%</Table.Cell></Table.Row>
        </Table.Body>
      </Table>
      <Message style={estiloDeClave(resultado.semaforo.clave)}>
        <strong>{resultado.semaforo.titulo}.</strong> {resultado.semaforo.detalle}
        {resultado.semaforo.notaMetodologica && <p style={{ fontStyle: "italic", marginTop: 6 }}>{resultado.semaforo.notaMetodologica}</p>}
      </Message>

      <Header as="h3">{t("water_balance_section_02")}</Header>
      <Message>{t("water_balance_methodology_body")}</Message>

      <Header as="h3">{t("water_balance_section_03")}</Header>
      <Table basic="very" compact>
        <Table.Body>
          <Table.Row>
            <Table.Cell width={5}>{t("water_balance_scope_period")}</Table.Cell>
            <Table.Cell>{period || SIN_DECLARAR} · {year || SIN_DECLARAR}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell width={5}>{t("water_balance_scope_typology")}</Table.Cell>
            <Table.Cell>
              {tipologiaCfg?.nombre || SIN_DECLARAR} — {tipologiaCfg?.descripcion}
              {typology === "C_PRODUCTO_TERMINADO" && !hasLaundry && ` (${t("water_balance_scope_no_laundry_note")})`}
            </Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell width={5}>{t("water_balance_scope_boundary")}</Table.Cell>
            <Table.Cell>{(centers || []).length ? centers.map((c) => c.nombre || c.id).join(", ") : SIN_DECLARAR}</Table.Cell>
          </Table.Row>
          <Table.Row><Table.Cell>{t("water_balance_operating_days_label")}</Table.Cell><Table.Cell>{context?.operatingDays || SIN_DECLARAR}</Table.Cell></Table.Row>
          <Table.Row><Table.Cell>{t("water_balance_staffing_label")}</Table.Cell><Table.Cell>{context?.staffing || SIN_DECLARAR}</Table.Cell></Table.Row>
          <Table.Row><Table.Cell>{t("water_balance_geo_label")}</Table.Cell><Table.Cell>{context?.geoNote || SIN_DECLARAR}</Table.Cell></Table.Row>
          <Table.Row><Table.Cell>{t("water_balance_wri_label")}</Table.Cell><Table.Cell>{context?.wriRisk || SIN_DECLARAR}</Table.Cell></Table.Row>
          <Table.Row><Table.Cell>{t("water_balance_wwf_label")}</Table.Cell><Table.Cell>{context?.wwfRisk || SIN_DECLARAR}</Table.Cell></Table.Row>
        </Table.Body>
      </Table>

      <Header as="h3">{t("water_balance_section_06")}</Header>
      {(executedFieldCases || []).length === 0 ? (
        <Message>{t("water_balance_no_cases_executed")}</Message>
      ) : (
        <Table celled compact>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>{t("water_balance_col_date")}</Table.HeaderCell>
              <Table.HeaderCell>{t("water_balance_col_case")}</Table.HeaderCell>
              <Table.HeaderCell>{t("water_balance_col_responsible")}</Table.HeaderCell>
              <Table.HeaderCell>{t("water_balance_col_grade")}</Table.HeaderCell>
              <Table.HeaderCell>{t("water_balance_col_evidence")}</Table.HeaderCell>
              <Table.HeaderCell>{t("water_balance_col_duration")}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {(executedFieldCases || []).map((c, i) => (
              <Table.Row key={i}>
                <Table.Cell>{new Date(c.date).toLocaleDateString("es-AR")}</Table.Cell>
                <Table.Cell>{CASOS_DE_CAMPO[c.caseId]?.nombre || c.caseId}</Table.Cell>
                <Table.Cell>{c.responsible}</Table.Cell>
                <Table.Cell>{c.gradeAwarded}</Table.Cell>
                <Table.Cell>{(c.evidence || []).length > 0 ? t("water_balance_files_count", { n: c.evidence.length }) : "—"}</Table.Cell>
                <Table.Cell>{c.durationMin >= 480 ? t("water_balance_one_day") : `${c.durationMin} min`}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      <Header as="h3">{t("water_balance_section_07")}</Header>
      {(instruments || []).length === 0 ? (
        <Message>{t("water_balance_no_instruments")}</Message>
      ) : (
        <Table celled compact>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>{t("water_balance_col_name")}</Table.HeaderCell>
              <Table.HeaderCell>{t("water_balance_col_role")}</Table.HeaderCell>
              <Table.HeaderCell>{t("water_balance_col_process")}</Table.HeaderCell>
              <Table.HeaderCell>{t("water_balance_col_calibration_due")}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {instruments.map((it, i) => (
              <Table.Row key={i}>
                <Table.Cell>{it.name}</Table.Cell>
                <Table.Cell>{it.meterRole}</Table.Cell>
                <Table.Cell>{procesoDeInstrumento(it.id)}</Table.Cell>
                <Table.Cell>{it.calibrationDue || "—"}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      <Header as="h3">{t("water_balance_section_08")}</Header>
      <Table celled compact>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>{t("water_balance_col_source")}</Table.HeaderCell>
            <Table.HeaderCell textAlign="right">m³</Table.HeaderCell>
            <Table.HeaderCell>{t("water_balance_col_declared_method")}</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {(inputs || []).map((f) => (
            <Table.Row key={f.sourceId}>
              <Table.Cell>{f.sourceId}</Table.Cell>
              <Table.Cell textAlign="right">{typeof f.volumeM3 === "number" ? f.volumeM3.toLocaleString("es-AR") : "—"}</Table.Cell>
              <Table.Cell>{f.declaredMethod}</Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>

      <Header as="h3">{t("water_balance_section_09")}</Header>
      <Table celled compact>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>{t("water_balance_col_center")}</Table.HeaderCell>
            <Table.HeaderCell>{t("water_balance_col_output")}</Table.HeaderCell>
            <Table.HeaderCell>{t("water_balance_col_origin")}</Table.HeaderCell>
            <Table.HeaderCell textAlign="right">m³</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {(outputs || []).map((s, i) => (
            <Table.Row key={i}>
              <Table.Cell>{s.centerId}</Table.Cell>
              <Table.Cell>{s.name}</Table.Cell>
              <Table.Cell>{s.origin}</Table.Cell>
              <Table.Cell textAlign="right">{(s.volumeM3 || 0).toLocaleString("es-AR")}</Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>

      <Header as="h3">{t("water_balance_section_11")}</Header>
      {(executedFieldCases || []).filter((c) => CASOS_DE_CAMPO[c.caseId]?.memoriaDeCalculo).length === 0 ? (
        <Message>{t("water_balance_no_calc_memory")}</Message>
      ) : (
        (executedFieldCases || [])
          .filter((c) => CASOS_DE_CAMPO[c.caseId]?.memoriaDeCalculo)
          .map((c, i) => {
            const caso = CASOS_DE_CAMPO[c.caseId];
            const formula = Array.isArray(caso.memoriaDeCalculo.formula) ? caso.memoriaDeCalculo.formula.join(" · ") : caso.memoriaDeCalculo.formula;
            return (
              <Segment key={i} size="small">
                <strong>{caso.nombre}</strong>
                <p style={{ fontFamily: "monospace", fontSize: 12 }}>{formula}</p>
              </Segment>
            );
          })
      )}

      <Header as="h3">{t("water_balance_section_12")}</Header>
      <Segment>
        <p style={{ fontFamily: "monospace" }}>
          {t("water_balance_col_entries").toUpperCase()} ({resultado.totalEntradas.toLocaleString("es-AR")}) = {t("water_balance_stat_outputs").toUpperCase()} ({resultado.totalSalidas.toLocaleString("es-AR")}) + Δ {t("water_balance_col_delta_inventory").toUpperCase()} ({resultado.deltaInventarioM3.toLocaleString("es-AR")}) {resultado.descuadre >= 0 ? "+" : ""}
          {resultado.descuadre.toLocaleString("es-AR")} {t("water_balance_stat_imbalance").toUpperCase()}
        </p>
      </Segment>

      <Header as="h3">{t("water_balance_section_13")}</Header>
      {resultado.descuadre <= 0 ? (
        <Message positive>{t("water_balance_no_diff_to_classify")}</Message>
      ) : (lossClassification || []).length === 0 ? (
        <Message negative>{t("water_balance_incomplete_unclassified", { vol: resultado.descuadre.toLocaleString("es-AR") })}</Message>
      ) : (
        <>
          <Table celled compact>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>{t("water_balance_col_category_m36")}</Table.HeaderCell>
                <Table.HeaderCell textAlign="right">m³</Table.HeaderCell>
                <Table.HeaderCell>{t("water_balance_note_placeholder")}</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {lossClassification.map((c, i) => (
                <Table.Row key={i}>
                  <Table.Cell>{CLASES_RESIDUO[c.category]?.nombre || c.category}</Table.Cell>
                  <Table.Cell textAlign="right">{(c.volumeM3 || 0).toLocaleString("es-AR")}</Table.Cell>
                  <Table.Cell>{c.note || "—"}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
          {avanceClasificacion < 100 && <Message negative>{t("water_balance_incomplete_partial_classified", { pct: avanceClasificacion.toFixed(0) })}</Message>}
        </>
      )}

      <Header as="h3">{t("water_balance_section_14")}</Header>
      <Statistic size="small" color={resultado.indiceCalidad >= META_INDICE_CALIDAD ? "green" : "orange"}>
        <Statistic.Value>{resultado.indiceCalidad.toFixed(2)}</Statistic.Value>
        <Statistic.Label>{t("water_balance_index_out_of_5")}</Statistic.Label>
      </Statistic>
      <p style={{ color: "#666", fontSize: 13 }}>{t("water_balance_index_derived_note")}</p>
      <p style={{ color: "#666", fontSize: 13 }}>
        {t(resultado.indiceCalidad >= META_INDICE_CALIDAD ? "water_balance_meets_quality_goal" : "water_balance_below_quality_goal", { meta: META_INDICE_CALIDAD.toFixed(2) })}.{" "}
        <em>{t("water_balance_quality_goal_disclaimer")}</em>
      </p>

      <Header as="h3">{t("water_balance_section_15")}</Header>
      {benchmarkDisponible ? (
        <Segment>
          <Dropdown selection options={PROCESO_OPTIONS} value={procesoId} onChange={(_, d) => setProcesoId(d.value)} />
          {evalIndicador.disponible ? (
            <p style={{ marginTop: 10 }}>
              {t("water_balance_specific_consumption")}: <strong>{esp?.toFixed(1)} L/kg</strong> — {t("water_balance_published_range")} {evalIndicador.min}–{evalIndicador.max} L/kg.{" "}
              {evalIndicador.dentroDelRango ? t("water_balance_within_range") : t("water_balance_out_of_range", { pct: evalIndicador.desviacionPct.toFixed(1) })}
            </p>
          ) : (
            <p style={{ marginTop: 10 }}>{evalIndicador.motivo}</p>
          )}
          <Message warning size="small">
            {ADVERTENCIAS_BENCHMARK_IFC.map((a, i) => (
              <p key={i}>{a}</p>
            ))}
          </Message>
        </Segment>
      ) : (
        <Message>{evalIndicador.motivo} {t("water_balance_trend_vs_baseline_note")}</Message>
      )}

      <Header as="h3">{t("water_balance_section_16")}</Header>
      <Table celled compact>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>{t("water_balance_col_coverage")}</Table.HeaderCell>
            <Table.HeaderCell textAlign="right">m³</Table.HeaderCell>
            <Table.HeaderCell textAlign="right">%</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {cobertura.map((c) => (
            <Table.Row key={c.categoria}>
              <Table.Cell>{COBERTURA_LABEL[c.categoria]}</Table.Cell>
              <Table.Cell textAlign="right">{c.volumenM3.toLocaleString("es-AR")}</Table.Cell>
              <Table.Cell textAlign="right">{c.pct}%</Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      <h5>{t("water_balance_pareto_title")}</h5>
      <Table celled compact>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>{t("water_balance_col_use")}</Table.HeaderCell>
            <Table.HeaderCell textAlign="right">m³</Table.HeaderCell>
            <Table.HeaderCell textAlign="right">%</Table.HeaderCell>
            <Table.HeaderCell textAlign="right">{t("water_balance_col_cumulative_pct")}</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {resultado.pareto.map((s) => (
            <Table.Row key={s.id} positive={s.dentroDel80}>
              <Table.Cell>{s.nombre}</Table.Cell>
              <Table.Cell textAlign="right">{s.volumenM3.toLocaleString("es-AR")}</Table.Cell>
              <Table.Cell textAlign="right">{s.pct.toFixed(1)}%</Table.Cell>
              <Table.Cell textAlign="right">{s.pctAcumulado.toFixed(1)}%</Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>

      <Header as="h3">{t("water_balance_costing_title")}</Header>
      <p style={{ color: "#666", fontSize: 13 }}>{t("water_balance_costing_no_section_note")}</p>
      {porCentro.length === 0 ? (
        <Message>{t("water_balance_no_outputs_to_cost")}</Message>
      ) : (
        <>
          <Table celled compact>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>{t("water_balance_col_center")}</Table.HeaderCell>
                <Table.HeaderCell textAlign="right">m³</Table.HeaderCell>
                <Table.HeaderCell textAlign="right">{t("water_balance_col_total")}</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {porCentro.map((c) => (
                <Table.Row key={c.centroId}>
                  <Table.Cell>{c.centroId}</Table.Cell>
                  <Table.Cell textAlign="right">{c.volumenM3.toLocaleString("es-AR")}</Table.Cell>
                  <Table.Cell textAlign="right">{c.total.toLocaleString("es-AR", { maximumFractionDigits: 0 })}</Table.Cell>
                </Table.Row>
              ))}
              <Table.Row>
                <Table.Cell><strong>{t("water_balance_total_period_cost_label")}</strong></Table.Cell>
                <Table.Cell />
                <Table.Cell textAlign="right"><strong>{totalCosteo.toLocaleString("es-AR", { maximumFractionDigits: 0 })}</strong></Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table>
          {perdidaCosteada.volumenM3 > 0 && (
            <Message negative>
              <Message.Header>{t("water_balance_real_loss_cost_header", { total: perdidaCosteada.total.toLocaleString("es-AR", { maximumFractionDigits: 0 }) })}</Message.Header>
              {t("water_balance_real_loss_cost_body", { vol: perdidaCosteada.volumenM3.toLocaleString("es-AR") })}
            </Message>
          )}
        </>
      )}

      <Header as="h3">{t("water_balance_section_20")}</Header>
      {resultado.planDeMedicion.length === 0 ? (
        <Message positive>{t("water_balance_all_grade5")}</Message>
      ) : (
        <Table celled compact>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>{t("water_balance_col_flow")}</Table.HeaderCell>
              <Table.HeaderCell textAlign="right">{t("water_balance_col_current_grade")}</Table.HeaderCell>
              <Table.HeaderCell textAlign="right">{t("water_balance_col_estimated_gain")}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {resultado.planDeMedicion.map((f) => (
              <Table.Row key={f.id}>
                <Table.Cell>{f.nombre}</Table.Cell>
                <Table.Cell textAlign="right">{f.grado}</Table.Cell>
                <Table.Cell textAlign="right">+{f.gananciaIndiceEstimada}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {(correctionProposals || []).length > 0 && (
        <>
          <Header as="h3">{t("water_balance_section_22")}</Header>
          <Message warning>{t("water_balance_proposals_hint")}</Message>
          <Table celled compact>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>{t("water_balance_col_source")}</Table.HeaderCell>
                <Table.HeaderCell>{t("water_balance_col_type")}</Table.HeaderCell>
                <Table.HeaderCell textAlign="right">{t("water_balance_col_current_value")}</Table.HeaderCell>
                <Table.HeaderCell textAlign="right">{t("water_balance_col_proposed_value")}</Table.HeaderCell>
                <Table.HeaderCell>{t("water_balance_col_status")}</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {correctionProposals.map((p, i) => (
                <Table.Row key={i}>
                  <Table.Cell>{p.sourceId}</Table.Cell>
                  <Table.Cell>{p.type}</Table.Cell>
                  <Table.Cell textAlign="right">{p.currentValue ?? "—"}</Table.Cell>
                  <Table.Cell textAlign="right">{p.proposedValue ?? "—"}</Table.Cell>
                  <Table.Cell><Label size="tiny">{p.status || "pendiente"}</Label></Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </>
      )}

      <Header as="h3">{t("water_balance_section_24")}</Header>
      <Table celled compact>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>{t("water_balance_col_requirement")}</Table.HeaderCell>
            <Table.HeaderCell>{t("water_balance_col_status")}</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {requisitos.map((r, i) => (
            <Table.Row key={i} positive={r.cubierto} warning={!r.cubierto}>
              <Table.Cell>
                {r.req}
                {r.nota && <div style={{ color: "#666", fontSize: 12 }}>{r.nota}</div>}
              </Table.Cell>
              <Table.Cell>{r.cubierto ? t("water_balance_covered") : t("water_balance_pending")}</Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>

      <Header as="h3">{t("water_balance_section_27")}</Header>
      <h5>{t("water_balance_published_backing_title")}</h5>
      <Table celled compact>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>{t("water_balance_col_item")}</Table.HeaderCell>
            <Table.HeaderCell>{t("water_balance_col_source_full")}</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {CON_RESPALDO_PUBLICADO.map((f, i) => (
            <Table.Row key={i}>
              <Table.Cell>{f.elemento}</Table.Cell>
              <Table.Cell>
                {f.origen}
                {f.nota && <div style={{ color: "#666", fontSize: 12 }}>{f.nota}</div>}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      <h5>{t("water_balance_own_criteria_title")}</h5>
      <Table celled compact>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>{t("water_balance_col_item")}</Table.HeaderCell>
            <Table.HeaderCell>{t("water_balance_col_origin")}</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {CRITERIO_PROPIO_SUSTEMIA.map((f, i) => (
            <Table.Row key={i}>
              <Table.Cell>{f.elemento}</Table.Cell>
              <Table.Cell>
                {f.origen}
                {f.nota && <div style={{ color: "#666", fontSize: 12 }}>{f.nota}</div>}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      <Message size="small">
        <strong>{t("water_balance_module_refuses_to")}</strong>
        <ul>
          {EL_MODULO_SE_NIEGA_A.map((n, i) => (
            <li key={i}>{n}</li>
          ))}
        </ul>
      </Message>

      <Header as="h3">{t("water_balance_section_28")}</Header>
      {!resultado.cierraOk ? (
        <Message negative>{t("water_balance_declaration_blocked")}</Message>
      ) : (
        <Segment>
          <Table basic="very" compact>
            <Table.Body>
              <Table.Row>
                <Table.Cell width={4}>{t("water_balance_col_responsible")}</Table.Cell>
                <Table.Cell><Input fluid value={declaracion.nombre} onChange={(e) => setDeclaracion({ ...declaracion, nombre: e.target.value })} /></Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>{t("water_balance_position_label")}</Table.Cell>
                <Table.Cell><Input fluid value={declaracion.cargo} onChange={(e) => setDeclaracion({ ...declaracion, cargo: e.target.value })} /></Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>{t("water_balance_date_label")}</Table.Cell>
                <Table.Cell><Input type="date" fluid value={declaracion.fecha} onChange={(e) => setDeclaracion({ ...declaracion, fecha: e.target.value })} /></Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table>
        </Segment>
      )}
    </div>
  );
}

export default InformeReport;
